/*
 * Teach-alias modal (batch 5 #7) — the one-click fix for "no master sheet row
 * matches CONCEPT" when the ticket name and the sheet row have drifted past the
 * matcher's safe fuzzy ceiling (the recurring YGBP-abbreviation case).
 *
 * Opens from a no-sheet-row hygiene flag or a failed prep ticket. Fetches
 * sheet-backed candidates (closest rows first), and shows the FULL target row
 * (BATCH / AD_NAMING / AD_FORMAT / row #) before the operator confirms — a
 * taught alias silently renames every future launch of this concept, so the
 * confirm must show exactly what it will launch as. Teaching is an upsert;
 * aliases are managed (and deletable) in the Aliases list.
 */

import { useEffect, useState } from "react";
import {
  Alert as MantineAlert,
  Button as MantineButton,
  Group,
  Modal,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import {
  useConceptAliasSuggest,
  useTeachConceptAlias,
} from "../../api/queries";
import { errorMessage } from "../../api/errors";

export interface TeachAliasModalProps {
  open: boolean;
  /** The product whose master sheet the alias targets. */
  productKey: string;
  /** The ticket concept being taught (the alias input, verbatim). */
  concept: string;
  onClose: () => void;
  /** Called after a successful teach (parent may hint at re-drive / next scan). */
  onTaught?: () => void;
}

export function TeachAliasModal({
  open,
  productKey,
  concept,
  onClose,
  onTaught,
}: TeachAliasModalProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const suggest = useConceptAliasSuggest(productKey, concept, open);
  const teach = useTeachConceptAlias();

  // A fresh open (possibly for a different concept) starts unpicked.
  useEffect(() => {
    if (open) {
      setPicked(null);
      teach.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open only
  }, [open, productKey, concept]);

  const data = suggest.data;
  const pickedRow = data?.candidates.find((c) => c.concept === picked) ?? null;

  const submit = () => {
    if (!picked) return;
    teach.mutate(
      { product_key: productKey, input_raw: concept, canonical_concept: picked },
      {
        onSuccess: () => {
          onTaught?.();
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      opened={open}
      onClose={() => {
        if (!teach.isPending) onClose();
      }}
      title="Teach concept alias"
      centered
      closeOnEscape={!teach.isPending}
      closeOnClickOutside={!teach.isPending}
      size="lg"
    >
      <Stack gap="sm">
        <Text fz="sm">
          Map ticket concept <strong>{concept}</strong> to the master-sheet row it
          should launch from. Every future launch, sheet write-back, and hygiene
          scan resolves it to that row.
        </Text>

        {suggest.isLoading && (
          <Text c="dimmed" fz="sm">
            Loading sheet rows…
          </Text>
        )}

        {suggest.isError && (
          <MantineAlert color="red" variant="light" role="alert">
            Couldn't load sheet candidates: {errorMessage(suggest.error)}
          </MantineAlert>
        )}

        {data && (
          <>
            {data.matched_tier === "alias" ? (
              <MantineAlert color="blue" variant="light">
                Already taught — currently resolves to{" "}
                <strong>{data.matched_concept}</strong> via a saved alias. Teaching
                again replaces it.
              </MantineAlert>
            ) : data.matched_tier ? (
              <MantineAlert color="yellow" variant="light">
                Currently matches <strong>{data.matched_concept}</strong> via the{" "}
                <em>{data.matched_tier}</em> tier. A taught alias overrides that
                match — only teach if it picks the wrong row.
              </MantineAlert>
            ) : (
              <Text c="dimmed" fz="sm">
                No sheet row currently matches this concept — pick the row it
                belongs to.
              </Text>
            )}

            <Select
              label="Master-sheet CONCEPT"
              description={
                data.candidates.length < data.total_concepts
                  ? `Closest rows first — showing ${data.candidates.length} of ${data.total_concepts} concepts (type to search)`
                  : "Closest rows first (type to search)"
              }
              placeholder="Pick the target row"
              searchable
              data={data.candidates.map((c) => ({ value: c.concept, label: c.concept }))}
              value={picked}
              onChange={setPicked}
              maxDropdownHeight={280}
              nothingFoundMessage="No sheet concept matches your search"
            />

            {pickedRow && (
              <MantineAlert color="gray" variant="light" title="This is the row it will launch as">
                <Stack gap={2}>
                  <Text fz="xs">
                    <strong>CONCEPT:</strong> {pickedRow.concept}
                  </Text>
                  <Text fz="xs">
                    <strong>BATCH:</strong> {pickedRow.batch ?? "—"}
                    {"   "}
                    <strong>FORMAT:</strong> {pickedRow.ad_format ?? "—"}
                    {"   "}
                    <strong>row #</strong>
                    {pickedRow.row_idx}
                  </Text>
                  <Text fz="xs">
                    <strong>AD_NAMING:</strong> {pickedRow.ad_naming ?? "—"}
                  </Text>
                </Stack>
              </MantineAlert>
            )}
          </>
        )}

        {teach.isError && (
          <MantineAlert color="red" variant="light" role="alert">
            Couldn't save the alias: {errorMessage(teach.error)}
          </MantineAlert>
        )}

        <Group justify="flex-end" gap="sm" mt="xs">
          <MantineButton
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={teach.isPending}
          >
            Cancel
          </MantineButton>
          <MantineButton
            onClick={submit}
            disabled={!picked}
            loading={teach.isPending}
          >
            Teach alias
          </MantineButton>
        </Group>
      </Stack>
    </Modal>
  );
}
